function comparisonValues(value) {
    if (Array.isArray(value)) return value.map((item) => String(item ?? '').trim());
    return String(value ?? '').split(',').map((item) => item.trim());
}

// Matches the same generic operators supported by showIf. Keeping this helper
// independent of React makes conditional labels usable in Add/Edit forms and
// straightforward to test without module- or field-specific code.
export function conditionMatches(condition, value) {
    if (!condition?.field) return false;
    switch (condition.operator ?? 'eq') {
        case 'eq': return String(value ?? '') === String(condition.value ?? '');
        case 'neq': return String(value ?? '') !== String(condition.value ?? '');
        case 'truthy': return value !== undefined && value !== null && value !== '' && value !== false;
        case 'falsy': return value === undefined || value === null || value === '' || value === false;
        case 'notEmpty': return Array.isArray(value) ? value.length > 0 : Boolean(value);
        case 'in': return comparisonValues(condition.value).includes(String(value ?? ''));
        case 'notIn': return !comparisonValues(condition.value).includes(String(value ?? ''));
        default: return false;
    }
}

export function configuredFieldLabel(field, watchedValue, combined = false) {
    const fallback = combined
        ? (field?.combineLabel ?? field?.label)
        : field?.label;
    const rule = field?.labelWhen;
    return conditionMatches(rule, watchedValue) && rule?.label
        ? rule.label
        : fallback;
}

// Flatten a showIf into its leaf conditions: the top-level {field,operator,value}
// plus any "add more" entries in conditions[]. Mirrors how the forms read it.
export function showIfConditions(showIf) {
    if (!showIf) return [];
    const leaf = showIf.field ? [showIf] : [];
    return [...leaf, ...(showIf.conditions ?? []).filter((c) => c?.field)];
}

// Evaluate a showIf against an arbitrary value source. readValue(fieldKey) lets
// the caller decide where values come from — live form state in the forms, or a
// saved record in the detail view — so one condition definition drives both.
export function evaluateShowIfWith(showIf, readValue) {
    const conditions = showIfConditions(showIf);
    if (!conditions.length) return true;
    const results = conditions.map((c) => conditionMatches(c, readValue(c.field)));
    return showIf.logic === 'or' ? results.some(Boolean) : results.every(Boolean);
}

// colWhen — conditionally override a field's grid column span, so one field can
// shrink to make room for a sibling that a showIf has just revealed (e.g. Work
// Authorization narrows when its expiry date appears). Config is a rule, or a
// list of rules, each {field, operator, value, col}; the first match wins.
// Display only — the field key, payload mapping and validation are untouched.
export function colWhenRules(field) {
    const raw = field?.colWhen;
    if (!raw) return [];
    return (Array.isArray(raw) ? raw : [raw])
        .filter((rule) => rule?.field && Number(rule?.col) > 0);
}

// readValue(rule) resolves the watched value for one rule — the caller owns path
// resolution so a rule can reference a sibling in the same addRow row or a
// top-level field, exactly like showIf.
export function resolveColSpan(field, fallbackSpan, readValue) {
    for (const rule of colWhenRules(field)) {
        if (conditionMatches(rule, readValue(rule))) return Number(rule.col);
    }
    return fallbackSpan;
}

export function conditionFieldPath(condition, siblingPrefix) {
    if (!condition?.field) return ['__noop_conditional_label__'];
    return siblingPrefix != null
        ? [...(Array.isArray(siblingPrefix) ? siblingPrefix : [siblingPrefix]), condition.field]
        : [condition.field];
}
